import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { MetricCard } from '../../components/ui/Card.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { showToast } from '../../components/ui/Toaster.jsx';
import { financeApi } from '../../services/financeApi.js';
import { formatCurrency, formatDate } from '../../utils/formatters.js';

import { EquipmentModal } from './EquipmentModal.jsx';
import { MaintenanceOrderModal } from './MaintenanceOrderModal.jsx';
import { MaintenanceOrderDetailModal } from './MaintenanceOrderDetailModal.jsx';
import { MaintenancePlanModal } from './MaintenancePlanModal.jsx';

const ORDER_VARIANT = { OPEN: 'warning', IN_PROGRESS: 'info', COMPLETED: 'success', CANCELLED: 'neutral' };
const PRIO_VARIANT = { LOW: 'neutral', MEDIUM: 'info', HIGH: 'warning', URGENT: 'danger' };
const EQ_VARIANT = { OPERATIONAL: 'success', DOWN: 'danger', RETIRED: 'neutral' };

export function MaintenancePage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [eqOpen, setEqOpen] = useState(false);
  const [orderOpen, setOrderOpen] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const equipment = useQuery({ queryKey: ['finance', 'equipment'], queryFn: () => financeApi.listEquipment().then((r) => r.items) });
  const orders = useQuery({ queryKey: ['finance', 'maintenance-orders'], queryFn: () => financeApi.listMaintenanceOrders().then((r) => r.items) });
  const plans = useQuery({ queryKey: ['finance', 'maintenance-plans'], queryFn: () => financeApi.listMaintenancePlans().then((r) => r.items) });
  const eq = equipment.data || [];
  const ord = orders.data || [];
  const pln = plans.data || [];

  const setEqStatus = useMutation({
    mutationFn: ({ id, status }) => financeApi.setEquipmentStatus(id, status),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['finance', 'equipment'] }),
  });
  const setPlanActive = useMutation({
    mutationFn: ({ id, active }) => financeApi.setMaintenancePlanActive(id, active),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['finance', 'maintenance-plans'] }),
  });
  const generateDue = useMutation({
    mutationFn: () => financeApi.generateDuePlans(),
    onSuccess: (data) => {
      showToast(t('pm.plan.generated', { n: data?.count ?? 0 }));
      queryClient.invalidateQueries({ queryKey: ['finance', 'maintenance-orders'] });
      queryClient.invalidateQueries({ queryKey: ['finance', 'maintenance-plans'] });
    },
    onError: (e) => showToast(e?.message || t('common.loadError')),
  });

  const orderCols = [
    { key: 'orderNumber', label: t('pm.col.number'), render: (r) => <span className="mono">{r.orderNumber}</span> },
    { key: 'equipment', label: t('pm.col.equipment'), render: (r) => <span><strong>{r.equipmentNumber}</strong> <span className="muted text-sm">{r.equipmentName}</span></span> },
    { key: 'title', label: t('pm.col.title') },
    { key: 'priority', label: t('pm.col.priority'), render: (r) => <Badge variant={PRIO_VARIANT[r.priority]}>{t(`pm.priority.${r.priority}`)}</Badge> },
    { key: 'cost', label: t('pm.col.cost'), align: 'right', render: (r) => <span className="mono num">{formatCurrency(r.totalCost)}</span> },
    { key: 'status', label: t('col.status'), render: (r) => <Badge variant={ORDER_VARIANT[r.status]}>{t(`pm.status.${r.status}`)}</Badge> },
    { key: 'actions', label: '', align: 'right', render: (r) => <Button size="sm" variant="ghost" onClick={() => setDetailId(r.id)}>{t('pm.action.view')}</Button> },
  ];

  const eqCols = [
    { key: 'equipmentNumber', label: t('pm.eq.number'), render: (r) => <span className="mono">{r.equipmentNumber}</span> },
    { key: 'name', label: t('pm.eq.name') },
    { key: 'location', label: t('pm.eq.location'), render: (r) => r.location || '-' },
    { key: 'asset', label: t('pm.eq.asset'), render: (r) => r.assetNumber || '-' },
    { key: 'status', label: t('col.status'), render: (r) => <Badge variant={EQ_VARIANT[r.status]}>{t(`pm.eqStatus.${r.status}`)}</Badge> },
    { key: 'actions', label: '', align: 'right', render: (r) => (r.status !== 'RETIRED'
      ? <Button size="sm" variant="ghost" disabled={setEqStatus.isPending} onClick={() => setEqStatus.mutate({ id: r.id, status: r.status === 'DOWN' ? 'OPERATIONAL' : 'DOWN' })}>{r.status === 'DOWN' ? t('pm.eq.markUp') : t('pm.eq.markDown')}</Button>
      : null) },
  ];

  const planCols = [
    { key: 'planNumber', label: t('pm.plan.number'), render: (r) => <span className="mono">{r.planNumber}</span> },
    { key: 'equipment', label: t('pm.col.equipment'), render: (r) => <span><strong>{r.equipmentNumber}</strong> <span className="muted text-sm">{r.equipmentName}</span></span> },
    { key: 'title', label: t('pm.col.title') },
    { key: 'interval', label: t('pm.plan.interval'), align: 'right', render: (r) => <span className="mono num">{r.intervalDays} {t('pm.plan.days')}</span> },
    { key: 'nextDue', label: t('pm.plan.nextDue'), render: (r) => formatDate(r.nextDue) },
    { key: 'active', label: t('pm.plan.active'), render: (r) => (
      <Button size="sm" variant={r.active ? 'secondary' : 'ghost'} disabled={setPlanActive.isPending} onClick={() => setPlanActive.mutate({ id: r.id, active: !r.active })}>
        {r.active ? t('pm.plan.on') : t('pm.plan.off')}
      </Button>
    ) },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('pm.title')}</h1>
          <p className="muted">{t('pm.subtitle')}</p>
        </div>
        <div className="page-actions">
          <Button variant="ghost" onClick={() => generateDue.mutate()} disabled={generateDue.isPending}>{t('pm.action.generateDue')}</Button>
          <Button variant="secondary" onClick={() => setEqOpen(true)}>{t('pm.action.newEquipment')}</Button>
          <Button variant="secondary" onClick={() => setPlanOpen(true)} disabled={eq.length === 0}>{t('pm.action.newPlan')}</Button>
          <Button variant="primary" onClick={() => setOrderOpen(true)} disabled={eq.length === 0}>{t('pm.action.newOrder')}</Button>
        </div>
      </header>

      <div className="card-grid">
        <MetricCard label={t('pm.metric.openOrders')} value={String(ord.filter((o) => o.status === 'OPEN' || o.status === 'IN_PROGRESS').length)} trendVariant="warning" />
        <MetricCard label={t('pm.metric.down')} value={String(eq.filter((e) => e.status === 'DOWN').length)} trendVariant={eq.some((e) => e.status === 'DOWN') ? 'danger' : 'success'} />
        <MetricCard label={t('pm.metric.equipment')} value={String(eq.length)} />
      </div>

      <div className="settings-divider">{t('pm.section.orders')}</div>
      <Table columns={orderCols} rows={ord} isLoading={orders.isLoading} emptyMessage={t('pm.orders.empty')} />

      <div className="settings-divider" style={{ marginTop: 'var(--space-5)' }}>{t('pm.section.plans')}</div>
      <Table columns={planCols} rows={pln} isLoading={plans.isLoading} emptyMessage={t('pm.plan.empty')} />

      <div className="settings-divider" style={{ marginTop: 'var(--space-5)' }}>{t('pm.section.equipment')}</div>
      <Table columns={eqCols} rows={eq} isLoading={equipment.isLoading} emptyMessage={t('pm.eq.empty')} />

      <EquipmentModal open={eqOpen} onClose={() => setEqOpen(false)} />
      <MaintenanceOrderModal open={orderOpen} onClose={() => setOrderOpen(false)} equipment={eq} />
      <MaintenancePlanModal open={planOpen} onClose={() => setPlanOpen(false)} equipment={eq} />
      <MaintenanceOrderDetailModal orderId={detailId} onClose={() => setDetailId(null)} />
    </section>
  );
}
